/**
 * IBM Cloud - Watson AI, Cloud Functions, Cloudant, Object Storage
 */
import axios from 'axios'
import { AlertCircle, Check, CheckCircle, ChevronDown, ChevronRight, ExternalLink, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface IBMResource {
  id: string
  name: string
  type: string
  status: 'active' | 'inactive' | 'deploying' | 'error'
  endpoint?: string
}

interface IBMStatus {
  connected: boolean
  api_key?: string
  region?: string
  resource_group?: string
  resources: IBMResource[]
}

const IBM_RESOURCES = [
  { id: 'functions', label: 'Cloud Functions', desc: 'Serverless compute (Node.js, Python, Go)', icon: '⚡' },
  { id: 'watson', label: 'Watson AI', desc: 'NLP, speech, vision, LLM inference', icon: '🤖' },
  { id: 'cloudant', label: 'Cloudant (NoSQL)', desc: 'JSON document database, CouchDB compatible', icon: '🗄️' },
  { id: 'cos', label: 'Cloud Object Storage', desc: 'S3-compatible object storage', icon: '📦' },
  { id: 'redis', label: 'Databases for Redis', desc: 'Managed Redis cache', icon: '⚡' },
]

const IBM_REGIONS = [
  'us-south (Dallas)', 'us-east (Washington DC)', 'eu-gb (London)',
  'eu-de (Frankfurt)', 'jp-tok (Tokyo)', 'au-syd (Sydney)',
]

const PRICING = [
  { service: 'Cloud Functions', price: '$0.000017/GB-s', note: '400K GB-s free/month' },
  { service: 'Watson AI', price: '$0.0025/1000 tokens', note: 'Lite plan: 30K tokens/month free' },
  { service: 'Cloudant', price: '$1.00/GB/month', note: '1GB free' },
  { service: 'Cloud Object Storage', price: '$0.023/GB/month', note: '25GB free' },
]

export default function IBMPage() {
  const [status, setStatus] = useState<IBMStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [openSection, setOpenSection] = useState<string | null>('setup')
  const [selectedResources, setSelectedResources] = useState<string[]>(['functions', 'watson', 'cloudant', 'cos'])
  const [form, setForm] = useState({ api_key: '', region: 'us-south (Dallas)', resource_group: 'Default' })

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/platforms/ibm`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStatus(resp.data as IBMStatus)
    } catch {
      setStatus({ connected: false, resources: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchStatus() }, [])

  const handleConnect = async () => {
    if (!form.api_key) { setError('API Key required'); return }
    setSaving(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/platforms/ibm`, form, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('IBM Cloud connected')
      setShowForm(false)
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Connection failed')
    } finally { setSaving(false) }
  }

  const handleDeploy = async () => {
    setDeploying(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/platforms/ibm/deploy`, { resources: selectedResources }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Deployment started')
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Deploy failed')
    } finally { setDeploying(false) }
  }

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/platforms/ibm`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Disconnected')
      void fetchStatus()
    } catch { setError('Failed') }
  }

  const toggleResource = (id: string) => {
    setSelectedResources(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    )
  }

  if (loading) {return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>}

  return (
    <div>
      {/* Baslik */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#1F70C1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 900, color: 'white' }}>
            IBM
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '2px' }}>IBM Cloud</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Watson AI, Cloud Functions, Cloudant, Object Storage</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {status?.connected && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#10b981' }}>
              <CheckCircle size={13} /> Connected
            </span>
          )}
          <a href="https://cloud.ibm.com/registration" target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#3b82f6', textDecoration: 'none' }}>
            <ExternalLink size={12} /> IBM Cloud
          </a>
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {/* Bagli degil */}
      {!status?.connected && !showForm && (
        <div style={{ padding: '32px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔵</div>
          <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>IBM Cloud not connected</p>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Connect your IBM Cloud account to deploy Watson AI, Cloud Functions, and more.
          </p>
          <button onClick={() => setShowForm(true)}
            style={{ padding: '10px 20px', background: '#1F70C1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Connect IBM Cloud
          </button>
        </div>
      )}

      {/* Baglanti formu */}
      {showForm && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Connect IBM Cloud</h3>

          {/* Adim 1 */}
          <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(31,112,193,0.06)', border: '1px solid rgba(31,112,193,0.2)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text-primary)' }}>How to get API Key:</strong><br />
            1. Go to <a href="https://cloud.ibm.com/iam/apikeys" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>IBM Cloud → Manage → IAM → API Keys</a><br />
            2. Click &quot;Create an IBM Cloud API key&quot;<br />
            3. Name it &quot;aicloud-key&quot;, click Create, copy the key
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>IBM Cloud API Key *</label>
              <input type="password" placeholder="Enter your IBM Cloud API key"
                value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Region</label>
              <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                {IBM_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Resource Group</label>
              <input type="text" placeholder="Default"
                value={form.resource_group}
                onChange={e => setForm(f => ({ ...f, resource_group: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleConnect() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#1F70C1', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Connecting...' : 'Connect'}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bagli - kaynak secimi ve deploy */}
      {status?.connected && (
        <>
          {/* Kaynak secimi */}
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '14px' }}>Select Resources to Deploy</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {IBM_RESOURCES.map(r => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: selectedResources.includes(r.id) ? 'rgba(31,112,193,0.06)' : 'var(--body-bg)', border: `1px solid ${selectedResources.includes(r.id) ? 'rgba(31,112,193,0.3)' : 'var(--border-color)'}`, borderRadius: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedResources.includes(r.id)} onChange={() => toggleResource(r.id)} />
                  <span style={{ fontSize: '16px' }}>{r.icon}</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.label}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <button onClick={() => { void handleDeploy() }} disabled={deploying || selectedResources.length === 0}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: '#1F70C1', color: 'white', border: 'none', borderRadius: '8px', cursor: deploying ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: deploying ? 0.6 : 1 }}>
              {deploying ? <><Loader size={14} /> Deploying...</> : 'Deploy Selected Resources'}
            </button>
          </div>

          {/* Mevcut kaynaklar */}
          {status.resources.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Deployed Resources</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {status.resources.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: r.status === 'active' ? '#10b981' : r.status === 'deploying' ? '#f59e0b' : '#ef4444', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.name}</div>
                      {r.endpoint && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{r.endpoint}</div>}
                    </div>
                    <span style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-secondary)' }}>{r.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => { void handleDisconnect() }}
            style={{ padding: '8px 14px', background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
            Disconnect IBM Cloud
          </button>
        </>
      )}

      {/* Fiyatlandirma */}
      <div style={{ marginTop: '20px' }}>
        <button onClick={() => setOpenSection(openSection === 'pricing' ? null : 'pricing')}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
          Pricing Overview
          {openSection === 'pricing' ? <ChevronDown size={14} style={{ marginLeft: 'auto' }} /> : <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
        </button>
        {openSection === 'pricing' && (
          <div style={{ padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
            {PRICING.map(p => (
              <div key={p.service} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ fontSize: '13px' }}>{p.service}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F70C1' }}>{p.price}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.note}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
